import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { useGetProfilesQuery } from '../../store/slices/adminSlice';
import { useAddDepartmentMutation, useUpdateDepartmentMutation } from '../../store/slices/departmentSlice';
import { sbg } from '../../theme/sbgTokens';

interface Department {
  id?: string;
  name: string;
  manager: string;
  isSalesTeam?: boolean;
}

interface DepartmentFormModalProps {
  onClose: () => void;
  department?: Department;
  isEdit?: boolean;
}

interface ManagerOption {
  id: string;
  name: string;
}

type FormErrors = Partial<Record<'name' | 'manager', string>>;

const SALES_TEAM_HINT_ID = 'department-form-sales-team-hint';

/** The API answers with `{ success: false, error }`; RTK Query puts it on `data`. */
const readSaveError = (error: unknown) => {
  const { status, data } = (error ?? {}) as { status?: number | string; data?: { error?: string; message?: string } };
  return { status, message: (data?.error || data?.message || '').toLowerCase() };
};

const DepartmentFormModal: React.FC<DepartmentFormModalProps> = ({ onClose, department, isEdit = false }) => {
  const [formData, setFormData] = useState<Department>({
    name: '',
    manager: '',
    isSalesTeam: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const managerInputRef = useRef<HTMLInputElement>(null);
  const {
    data: profiles = [],
    isLoading: profilesLoading,
    isError: profilesError,
    refetch: refetchProfiles,
  } = useGetProfilesQuery();
  const [addDepartment, { isLoading: isAdding }] = useAddDepartmentMutation();
  const [updateDepartment, { isLoading: isUpdating }] = useUpdateDepartmentMutation();
  const isSubmitting = isAdding || isUpdating;

  useEffect(() => {
    if (department && isEdit) {
      setFormData({
        name: department.name || '',
        manager: (department as any).manager || '',
        isSalesTeam: Boolean((department as any).isSalesTeam),
      });
    }
  }, [department, isEdit]);

  const managerOptions = useMemo<ManagerOption[]>(
    () => profiles
      .filter((profile) =>
        profile.role === 'admin' || profile.role === 'hr' || profile.role === 'manager'
      )
      .map((profile) => ({
        id: profile.id,
        name: profile.fullName || profile.email,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [profiles]
  );

  /* A saved manager who no longer has a manager-level account still shows as the current value. */
  const options = useMemo<ManagerOption[]>(() => {
    const current = formData.manager.trim();
    if (!current || managerOptions.some((option) => option.name === current)) return managerOptions;
    return [{ id: `current:${current}`, name: current }, ...managerOptions];
  }, [formData.manager, managerOptions]);

  const managerValue = formData.manager
    ? options.find((option) => option.name === formData.manager.trim()) ?? null
    : null;

  const clearError = (field: keyof FormErrors) => {
    if (!errors[field]) return;
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Enter a department name.';
    }

    if (!formData.manager.trim()) {
      newErrors.manager = 'Choose who manages this department.';
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = e.target.name as keyof FormErrors;
    const value = e.target.value;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    clearError(name);
  };

  const handleSubmit = async () => {
    const newErrors = validateForm();
    /* Move focus to the first field that needs attention. */
    if (newErrors.name) {
      nameInputRef.current?.focus();
      return;
    }
    if (newErrors.manager) {
      managerInputRef.current?.focus();
      return;
    }

    const selectedManager = managerOptions.find((option) => option.name === formData.manager.trim());
    const payload = {
      name: formData.name.trim(),
      manager: formData.manager.trim(),
      managerUserId: selectedManager?.id || null,
      isSalesTeam: Boolean(formData.isSalesTeam),
    };

    setSaveError(null);
    try {
      if (isEdit && department?.id) {
        await updateDepartment({ id: department.id, department: payload }).unwrap();
      } else if (isEdit && (department as any)?._id) {
        await updateDepartment({ id: (department as any)._id, department: payload }).unwrap();
      } else {
        await addDepartment(payload).unwrap();
      }
      onClose();
    } catch (error) {
      console.error('Error saving department:', error);
      const { status, message } = readSaveError(error);
      if (message.includes('already exists')) {
        setErrors((prev) => ({ ...prev, name: `A department named “${payload.name}” already exists. Choose another name.` }));
        nameInputRef.current?.focus();
      } else if (message.includes('manager-capable') || message.includes('manager user not found')) {
        setErrors((prev) => ({ ...prev, manager: `${payload.manager} can’t lead a department. Choose an admin, HR or manager account.` }));
        managerInputRef.current?.focus();
      } else if (status === 403) {
        setSaveError('Only HR and admins can change departments. Ask HR to make this change.');
      } else if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
        setSaveError('Couldn’t reach the server. Check your connection and try again; your changes are still here.');
      } else {
        setSaveError('Couldn’t save the department. Try again; your changes are still here.');
      }
    }
  };

  const managerHelper = errors.manager
    ? errors.manager
    : profilesError
      ? (
        <>
          Manager accounts didn’t load.{' '}
          <Link component="button" type="button" variant="caption" onClick={() => { refetchProfiles(); }} sx={{ verticalAlign: 'baseline' }}>
            Try again
          </Link>
        </>
      )
      : profilesLoading
        ? 'Loading manager accounts…'
        : 'Admins, HR and managers can lead a department.';

  const submitLabel = isEdit ? 'Save changes' : 'Add department';
  const submittingLabel = isEdit ? 'Saving changes…' : 'Adding department…';

  return (
    <Box
      component="form"
      noValidate
      sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        if (!isSubmitting && !profilesLoading && !profilesError) handleSubmit();
      }}
    >
      <DialogTitle>
        {isEdit ? 'Edit department' : 'Add department'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: sbg.text.secondary, mb: 2.5 }}>
          {isEdit
            ? 'Changes apply to approvals and reports right away.'
            : 'Departments group people for approvals, time off and reports.'}
        </Typography>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
          <TextField
            name="name"
            label="Department name"
            placeholder="e.g. Underwriting"
            fullWidth
            required
            autoFocus={!isEdit}
            inputRef={nameInputRef}
            value={formData.name}
            onChange={handleInputChange}
            error={!!errors.name}
            helperText={errors.name || ' '}
          />

          {/* Searchable: the list holds every admin, HR and manager account. */}
          <Autocomplete<ManagerOption, false, false, false>
            options={options}
            value={managerValue}
            onChange={(_event, option) => {
              setFormData((prev) => ({ ...prev, manager: option?.name ?? '' }));
              clearError('manager');
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            renderOption={(props, option) => {
              const { key: _key, ...optionProps } = props as typeof props & { key?: React.Key };
              return (
                <li key={option.id} {...optionProps}>
                  {option.name}
                </li>
              );
            }}
            loading={profilesLoading}
            loadingText="Loading manager accounts…"
            noOptionsText="No admin, HR or manager account matches"
            disabled={profilesLoading || profilesError}
            fullWidth
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                name="manager"
                label="Manager"
                required
                inputRef={managerInputRef}
                placeholder={profilesLoading ? 'Loading…' : 'Search by name'}
                error={!!errors.manager}
                helperText={managerHelper}
              />
            )}
          />
        </Box>

        <Box
          sx={{
            mt: 1,
            p: 2,
            borderRadius: sbg.radius.lg,
            bgcolor: sbg.bg.surfaceSubtle,
            display: 'grid',
            gap: 0.5,
          }}
        >
          {/* A checkbox, not a switch: this only takes effect when the form is saved. */}
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(formData.isSalesTeam)}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setFormData(prev => ({ ...prev, isSalesTeam: checked }));
                }}
                inputProps={{ 'aria-describedby': SALES_TEAM_HINT_ID }}
              />
            }
            label="Sales team"
          />
          {/* Indented to start under the label text (24px: checkbox + gap). */}
          <Typography id={SALES_TEAM_HINT_ID} variant="body2" sx={{ color: sbg.text.secondary, pl: 3 }}>
            Sales teams send WFH approvals to the sales VPs and are included in weekend shifts and office check-ins.
          </Typography>
        </Box>

        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || profilesLoading || profilesError}
          aria-busy={isSubmitting || undefined}
          startIcon={isSubmitting ? <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" /> : undefined}
        >
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default DepartmentFormModal;
